import React from "react";
import _ from "lodash";
import s from "./emailAccountRow.scss";
import PropTypes from "prop-types";
import cx from "classnames";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import DropdownMenu from "Components/dropdownMenu/DropdownMenu";
import LoadingBar from "Components/loadingBar/LoadingBar";
import { calcPercentageAndRound } from "Utils/bytesFormats";
import { displayFormattedDate } from "Utils/dateFormats";
import t from "Utils/translation";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import { trash, registerColor, more, enable, disable } from "Icons/index";
import { withRouter, Link } from "react-router-dom";

const ContextTypes = {
  isLoggedUserAdmin: PropTypes.func,
  getLoggedUser: PropTypes.func,
  isMobileDevice: PropTypes.func,
  isMediaMobile: PropTypes.func,
  isMediaSM: PropTypes.func,
  isMediaMD: PropTypes.func,
  isMediaLG: PropTypes.func,
};

class EmailAccountRow extends React.Component {
  constructor(props, context) {
    super(props);
    const loggedUser = context.getLoggedUser();
    this.state = {
      isMenuOpen: false,
      isAdmin: context.isLoggedUserAdmin(),
      isPostmaster: loggedUser.type === "Postmaster",
      isLoggedUserManager: loggedUser.type === "Manager",
    };
  }

  setDropdownMenuOpen = (newVal) => {
    this.setState({ isMenuOpen: newVal });
  };

  goTo = (resource, domainCode, customerCode = null) => {
    let nextUrl;
    if (this.props.isLoggedUserAdmin) {
      nextUrl = `/customers/${customerCode}/domains/${domainCode}/${resource}`;
    } else {
      nextUrl = `/domains/${domainCode}/${resource}`;
    }

    this.props.history.push({
      pathname: nextUrl,
      state: {
        prevPagination: this.props.location.search,
      },
    });
  };

  showEmail = (code) => {
    const { pathname } = this.props.location;
    // postmaster use path like this "/email_account/:code"
    let basePath = pathname === "/" ? "/email_account" : pathname;
    this.props.history.push({
      pathname: `${basePath}/${code}/settings`,
      state: {
        prevPagination: this.props.location.search,
      },
    });
  };

  render() {
    const { emailAccount: ea, openDisableModal, openEnableModal } = this.props;
    const { isLoggedUserManager, isAdmin, isPostmaster } = this.state;
    const { isMobileDevice, isMediaMobile, isMediaSM, isMediaMD, isMediaLG } = this.context;

    const loggedUser = this.context.getLoggedUser();

    let showCleanDB = false;
    let canDisable = true;
    let canEnable = true;
    let canDelete = true;
    let hideMenu = ea.status === "disabled" && ea.status_detail === "admin" && !isAdmin;

    if (ea.status === "disabled" && (ea.status_detail === "admin" || ea.status_detail === "system") && !isAdmin) {
      canEnable = false;
    }

    if (
      isLoggedUserManager &&
      ea.status === "disabled" &&
      ea.status_detail !== "manager" &&
      ea.status_detail !== "postmaster"
    ) {
      canEnable = false;
    }

    if (isPostmaster && ea.status === "disabled" && ea.status_detail !== "postmaster") {
      canEnable = false;
    }

    if (loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("disable_email_accounts")) {
      canDisable = false;
    }

    let quotaInGB, maxQuotaInGB;
    if (ea.max_email_quota === 52428800) {
      quotaInGB = _.round(ea.quota / 1048576, 2);
      maxQuotaInGB = "50 MB";
    } else {
      quotaInGB = _.round(ea.quota / 1073741824, 2);
      maxQuotaInGB = `${ea.max_email_quota / 1073741824} GB`;
    }

    if (loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("delete_email_accounts")) {
      canDelete = false;
    }

    const percentage = calcPercentageAndRound(ea.quota, ea.max_email_quota);
    let showMenu = ea.status !== "failure" && ea.status !== "updating" && !hideMenu;

    // hide button if dropdown is empty
    if (ea.status === "disabled" && !canDelete && !canEnable) showMenu = false;
    if (ea.status === "enabled" && !canDelete && !canDisable) showMenu = false;

    // is is in failure but is admin, show the clean_db button
    if (isAdmin) {
      showMenu = true;
      if (ea.status === "failure") showCleanDB = true;
      if (ea.status === "updating") canDelete = false;
    }

    return (
      <tr key={ea.code} className={cx({ [s.disabled]: ea.status === "disabled" })}>
        <td data-label={t("table.header.status")} className={s.justifyCenter}>
          <DomainStatusBadge status={ea.status} isHealthy statusDetail={ea.status_detail} />
        </td>
        <td data-label={t("table.header.account")} className="textOverflow">
          {ea.status === "updating" ? (
            ea.name + "@" + ea.domain_name
          ) : (
            <span className="internalLink" onClick={() => this.showEmail(ea.code, ea.customer_code)}>
              {ea.name + "@" + ea.domain_name}
            </span>
          )}
        </td>
        <td data-label={t("table.header.quote")}>
          <div className={s.quote}>
            <div className={s.percentage}>{percentage}</div>
            {!isMediaMobile() && <LoadingBar pctg={percentage} />}
            {!isMediaMobile() && <div className={s.giga}>{quotaInGB + "/" + maxQuotaInGB}</div>}
          </div>
        </td>
        <td data-label={t("table.header.lastAccess")} className="hideOnMobile">
          {ea.lastauth_datetime ? `${displayFormattedDate(ea.lastauth_datetime)} via ${ea.lastauth_protocol}` : "-"}
        </td>
        <td data-label={t("table.header.messagesIn24H")} className="hideOnMobile">
          {ea.outbound_limit_status}
        </td>
        <td className={s.justifyCenter}>
          <DropdownMenu
            isOpen={this.state.isMenuOpen}
            onOpenChange={this.setDropdownMenuOpen}
            hideToggle={!showMenu}
            actions={[
              {
                isHidden: !canDisable || ea.status !== "enabled",
                onClick: () => openDisableModal(ea.code, ea.name + "@" + ea.domain_name),
                icon: disable,
                label: t("ddMenu.disable"),
              },
              {
                isHidden: !canEnable || ea.status !== "disabled",
                onClick: () => openEnableModal(ea.code, ea.name + "@" + ea.domain_name),
                icon: enable,
                label: t("ddMenu.enable"),
              },
              {
                isHidden: !canDelete && !showCleanDB,
                onClick: () => this.props.deleteEmail(ea.code),
                icon: trash,
                label: showCleanDB ? "Pulisci database" : t("ddMenu.delete"),
              },
              {
                isHidden: !isAdmin,
                onClick: () => {
                  this.props.history.push(`/audits?target=${ea.code}`);
                },
                icon: registerColor,
                label: "Mostra in audit",
              },
            ]}
          >
            <ButtonIcon icon={more} />
          </DropdownMenu>
        </td>
      </tr>
    );
  }
}

EmailAccountRow.contextTypes = ContextTypes;

export default withRouter(EmailAccountRow);
